import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { toast } from 'react-toastify';

const BaseAssignmentModal = ({
  open,
  onClose,
  onAssign,
  loading,
  assignedItems = [],
  fetchItems,
  title,
  itemNameKey = 'name',
  itemSecondaryKey = null,
  itemIdKey = '_id',
  singleSelect = false,
  icon: Icon = null,
  searchPlaceholder = null,
  filterFlagKey = null,
  filterFlagLabel = '',
}) => {
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // A flag filter (e.g. "Nur Leitung") starts switched on so the dialog opens
  // on a short list; the search reaches everyone.
  const [flagOnly, setFlagOnly] = useState(true);

  useEffect(() => {
    setSearchTerm('');
    setFlagOnly(true);
    if (open) {
      loadItems();
    } else {
      // Reset selection when modal closes
      setSelectedItems([]);
    }
  }, [open]);

  useEffect(() => {
    // Pre-select assigned items when items are loaded
    if (items.length > 0 && assignedItems.length > 0) {
      const assignedItemObjects = items.filter(item =>
        assignedItems.some(assignedId => assignedId === item[itemIdKey])
      );
      setSelectedItems(assignedItemObjects);
    }
  }, [items, assignedItems]);

  const loadItems = async () => {
    setIsLoading(true);
    try {
      const response = await fetchItems();
      // Handle both direct arrays and objects with items property
      const itemsArray = Array.isArray(response) ? response : response.items || [];
      setItems(itemsArray);
    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Fehler beim Laden der Elemente');
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemToggle = item => {
    setSelectedItems(prev => {
      const isSelected = prev.some(i => i[itemIdKey] === item[itemIdKey]);

      if (singleSelect) {
        // For single select, just set the new item
        return isSelected ? [] : [item];
      } else {
        // For multi select, toggle the item
        if (isSelected) {
          return prev.filter(i => i[itemIdKey] !== item[itemIdKey]);
        } else {
          return [...prev, item];
        }
      }
    });
  };

  // Anyone already selected stays visible whatever the filters say — otherwise
  // saving would silently drop an entry the dialog no longer lists.
  const query = searchTerm.trim().toLowerCase();
  const visibleItems = items.filter(item => {
    if (selectedItems.some(selected => selected[itemIdKey] === item[itemIdKey])) return true;
    if (filterFlagKey && flagOnly && !item[filterFlagKey]) return false;
    if (!query) return true;
    const secondary = itemSecondaryKey ? item[itemSecondaryKey] : '';
    const haystack = [item[itemNameKey], Array.isArray(secondary) ? secondary.join(' ') : secondary]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(query);
  });
  const hiddenCount = items.length - visibleItems.length;

  const handleAssign = () => {
    if (singleSelect) {
      // For single select, pass the single ID or null
      onAssign(selectedItems[0]?.[itemIdKey] || null);
    } else {
      // For multi select, pass array of IDs
      onAssign(selectedItems.map(item => item[itemIdKey]));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {searchPlaceholder && (
              <TextField
                size='small'
                fullWidth
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                sx={{ mb: 1 }}
              />
            )}
            {filterFlagKey && (
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={flagOnly}
                    onChange={e => setFlagOnly(e.target.checked)}
                  />
                }
                label={
                  flagOnly && hiddenCount > 0 && !query
                    ? `${filterFlagLabel} (${hiddenCount} weitere ausgeblendet)`
                    : filterFlagLabel
                }
                sx={{ mb: 1 }}
              />
            )}
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {selectedItems.length} ausgewählt
            </Typography>
            {visibleItems.length === 0 && (
              <Typography variant='body2' color='text.secondary' sx={{ py: 3 }}>
                Keine Einträge gefunden.
              </Typography>
            )}
            <List>
              {visibleItems.map(item => (
                <ListItemButton
                  key={item[itemIdKey]}
                  selected={selectedItems.some(i => i[itemIdKey] === item[itemIdKey])}
                  onClick={() => handleItemToggle(item)}
                >
                  {Icon && (
                    <ListItemIcon>
                      <Icon />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={item[itemNameKey]}
                    secondary={
                      itemSecondaryKey && Array.isArray(item[itemSecondaryKey])
                        ? item[itemSecondaryKey].join(', ') || undefined
                        : itemSecondaryKey
                          ? item[itemSecondaryKey] || undefined
                          : undefined
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button onClick={handleAssign} disabled={loading} variant='contained' color='primary'>
          {loading ? 'Wird zugewiesen...' : 'Zuweisen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BaseAssignmentModal;
